import { z } from 'zod';
import { isoTimestamp } from './common';

export const metricsQuerySchema = z.object({
  /** Inclusive lower / exclusive upper bound on the run start time (default: the last 24 hours). */
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  agentId: z.uuid().optional(),
});
export type MetricsQuery = z.infer<typeof metricsQuerySchema>;

const latencySchema = z.object({
  avg: z.number().nullable(),
  p50: z.number().nullable(),
  p95: z.number().nullable(),
  p99: z.number().nullable(),
});

export const metricsDtoSchema = z.object({
  from: isoTimestamp,
  to: isoTimestamp,
  /** `minute` up to two hours, `hour` up to two days, otherwise `day`. */
  bucket: z.enum(['minute', 'hour', 'day']),
  totals: z.object({
    runs: z.number().int(),
    succeeded: z.number().int(),
    failed: z.number().int(),
    /** Failed, timed out or cancelled share of finished runs (0–1). */
    errorRate: z.number().nullable(),
    latencyMs: latencySchema,
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
    toolCalls: z.number().int(),
    workflowRuns: z.number().int(),
    workflowFailures: z.number().int(),
  }),
  series: z.array(
    z.object({
      bucket: isoTimestamp,
      runs: z.number().int(),
      failed: z.number().int(),
      p95LatencyMs: z.number().nullable(),
      tokens: z.number().int(),
    }),
  ),
  byAgent: z.array(
    z.object({
      agentId: z.uuid(),
      name: z.string(),
      runs: z.number().int(),
      failed: z.number().int(),
      p95LatencyMs: z.number().nullable(),
      tokens: z.number().int(),
    }),
  ),
  byModel: z.array(
    z.object({
      model: z.string(),
      runs: z.number().int(),
      avgLatencyMs: z.number().nullable(),
      tokens: z.number().int(),
    }),
  ),
  tools: z.array(
    z.object({ name: z.string(), calls: z.number().int(), failures: z.number().int() }),
  ),
  errors: z.array(z.object({ code: z.string(), count: z.number().int() })),
  scores: z.array(
    z.object({
      scorerId: z.uuid(),
      name: z.string(),
      count: z.number().int(),
      average: z.number().nullable(),
    }),
  ),
});
export type MetricsDto = z.infer<typeof metricsDtoSchema>;
